import React, { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import DataTable from 'react-data-table-component';
import { useGetCustomerById } from '../../../services/customerService';
import { useGetDocumentsByCustomer, useUploadDocument, useSubmitLoanDocuments } from '../../../services/loanService';
import { downloadDocument } from '../../../services/documentService';
import { UploadDocumentBodyDocumentType } from '../../../services/schemas/loanServiceSchemas/uploadDocumentBodyDocumentType';
import type { Customer } from '../../../services/schemas/customerServiceSchemas';
import type { DocumentReference } from '../../../services/schemas/loanServiceSchemas/documentReference';
import './CustomerDocuments.css';
import DocumentValidityBadge from '../../common/DocumentValidityBadge';
import DocumentActionsMenu from '../../common/DocumentActionsMenu';
import { DocumentPreviewModal, useDocumentPreview } from '../../common/DocumentPreview';
import { getDocumentUploadDate } from '../../../utils/documentDates';

interface DisplayDocument {
  id: string;
  documentType: string;
  documentKey: string;
  name: string;
  size: string;
  uploadDate?: string;
}

const CustomerDocuments: React.FC = () => {
  const documentPreview = useDocumentPreview(message => toast.error(message));
  const { id } = useParams<{ id: string }>();
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [downloadingDocuments, setDownloadingDocuments] = useState<Set<string>>(new Set());
  const navigate = useNavigate();

  // Add loading overlay state
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);

  // Fetch customer details
  const { data: customer, isLoading: isLoadingCustomer } = useGetCustomerById(id || '', {
    query: {
      enabled: !!id
    }
  });

  // Fetch documents using the customer ID
  const { data: documents, isLoading: isLoadingDocuments, refetch: refetchDocuments } = useGetDocumentsByCustomer(id || '', {
    query: {
      enabled: !!id
    }
  });

  // Upload document mutation
  const uploadDocumentMutation = useUploadDocument();

  // Submit documents mutation
  const submitDocumentsMutation = useSubmitLoanDocuments();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>, documentType: string) => {
    if (!e.target.files || e.target.files.length === 0) return;

    try {
      setIsUploading(true);
      setShowLoadingOverlay(true);
      setUploadError(null);
      const file = e.target.files[0];

      if (file.size > 100 * 1024 * 1024) {
        toast.error(`File size exceeds 100MB limit: ${file.name}`);
        return;
      }

      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        toast.error(`Invalid file type: ${file.name}. Please upload PDF, JPG, or PNG files.`);
        return;
      }

      // Generate a unique filename
      const timestamp = new Date().getTime().toString().slice(-4);
      const fileExtension = file.name.split('.').pop();
      const baseName = file.name.substring(0, file.name.lastIndexOf('.'));
      const uniqueFileName = `${baseName}_${timestamp}.${fileExtension}`;

      // Create a new File object with the unique name
      const uniqueFile = new File([file], uniqueFileName, { type: file.type });

      // Upload the document
      await uploadDocumentMutation.mutateAsync({
        loanApplicationId: id || '',
        data: {
          file: uniqueFile,
          documentType: documentType as UploadDocumentBodyDocumentType,
          description: `Uploaded ${documentType.toLowerCase().replace(/_/g, ' ')}`
        }
      });

      // Wait for the documents to be refetched
      await refetchDocuments();

      toast.success('File uploaded successfully');

    } catch (error) {
      console.error('Error uploading file:', error);
      const errorMessage = error instanceof Error ? error.message : 'An error occurred while uploading the file';
      setUploadError(errorMessage);
      toast.error('Failed to upload file: ' + errorMessage);
    } finally {
      setIsUploading(false);
      setShowLoadingOverlay(false);
      if (e.target) {
        e.target.value = '';
      }
    }
  };

  const handleSubmitDocuments = async () => {
    try {
      setShowLoadingOverlay(true);
      await submitDocumentsMutation.mutateAsync({ loanApplicationId: id || '' });
      setIsSubmitted(true);
      toast.success('Documents submitted successfully');
    } catch (error) {
      console.error('Error submitting documents:', error);
      toast.error('Failed to submit documents');
    } finally {
      setShowLoadingOverlay(false);
    }
  };

  const handleDownloadDocument = async (documentKey: string, documentName: string) => {
    try {
      // Add document to downloading set
      setDownloadingDocuments(prev => new Set(prev).add(documentKey));

      // Download the document
      const blob = await downloadDocument(documentKey);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = documentName;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Document downloaded successfully');
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Failed to download document');
    } finally {
      // Remove document from downloading set
      setDownloadingDocuments(prev => {
        const newSet = new Set(prev);
        newSet.delete(documentKey);
        return newSet;
      });
    }
  };

  const formatDocument = (doc: DocumentReference): DisplayDocument => {
    return {
      id: doc.id || '',
      documentType: doc.documentType,
      documentKey: doc.documentKey || '',
      name: doc.name || 'Unknown',
      size: doc.size + ' B',
      // When the file first entered the LMS. Absent only on documents stored before it was
      // recorded, which the table and the validity badge both render as unknown.
      uploadDate: getDocumentUploadDate(doc)
    };
  };

  const getDocumentByType = (type: string) => {
    const doc = documents?.find((doc: DocumentReference) => doc.documentType === type);
    return doc ? formatDocument(doc) : null;
  };

  // Add function to check if all required documents are uploaded
  const areAllRequiredDocumentsUploaded = () => {
    const requiredDocumentTypes = [
      'COMPANY_REGISTRATION_CERTIFICATE',
      'COMPANY_REP_ID_COPY',
      'BANK_LETTER',
      'PROOF_OF_ADDRESS',
      'TAX_CLEARANCE'
    ];
    return requiredDocumentTypes.every(type => getDocumentByType(type) !== null);
  };

  // DataTable columns configuration
  const columns = [
    {
      name: 'Document Type',
      selector: (row: any) => row.documentType,
      sortable: true,
      cell: (row: any) => (
        <div className="fw-bold">
          {row.documentType.replace(/_/g, ' ').toLowerCase()}
        </div>
      ),
    },
    {
      name: 'File Name',
      selector: (row: any) => row.name,
      sortable: true,
    },
    {
      name: 'File Size',
      selector: (row: any) => row.size,
      sortable: true,
    },
    {
      name: 'Upload Date',
      selector: (row: any) => row.uploadDate,
      sortable: true,
      format: (row: any) => {
        if (!row.uploadDate) return 'Unknown';
        const uploaded = new Date(row.uploadDate);
        return Number.isNaN(uploaded.getTime())
          ? 'Unknown'
          : uploaded.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
      },
    },
    {
      name: 'Validity',
      selector: (row: any) => row.uploadDate,
      sortable: true,
      cell: (row: any) => <DocumentValidityBadge uploadDate={row.uploadDate} />,
    },
    {
      name: 'Actions',
      cell: (row: any) => (
        <DocumentActionsMenu
          id={row.id || row.documentKey}
          actions={[
            {
              label: 'View',
              icon: 'fa-eye',
              busy: documentPreview.openingKey === row.documentKey,
              disabled: !row.documentKey,
              onClick: () => documentPreview.open(row.documentKey, row.name)
            },
            {
              label: 'Download',
              icon: 'fa-download',
              busy: downloadingDocuments.has(row.documentKey),
              disabled: !row.documentKey,
              onClick: () => handleDownloadDocument(row.documentKey, row.name)
            }
          ]}
        />
      ),
    },
  ];

  // Transform documents data for DataTable
  const documentsData = documents?.map(formatDocument) || [];

  // Display submission success state
  if (isSubmitted) {
    return (
      <div className="container-fluid p-4">
        <div className="card">
          <div className="card-body text-center py-5">
            <div className="mb-4">
              <i className="fa fa-check-circle text-success" style={{ fontSize: '4rem' }}></i>
            </div>
            <h2 className="mb-3">Documents Submitted Successfully</h2>
            <p className="text-muted mb-4">The customer documents have been submitted successfully.</p>
            <button
              className="btn btn-primary mt-3"
              onClick={() => navigate('/loan-customers')}
            >
              <i className="fa fa-arrow-left me-2"></i>
              Return to Customers List
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isLoadingCustomer || isLoadingDocuments) {
    return (
      <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
        <div className="container-fluid">
          <div className="row">
            <div className="col-12 text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3">Loading document details...</p>
              <small className="text-muted">
                {isLoadingCustomer ? 'Fetching customer details...' : 'Loading documents...'}
              </small>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="customer-documents">
      {/* Add loading overlay */}
      {showLoadingOverlay && (
        <div className="loading-overlay">
          <div className="loading-content">
            <div className="loading-spinner"></div>
            <div className="loading-text">Processing your request</div>
            <div className="loading-subtext">Please wait while we handle your document</div>
          </div>
        </div>
      )}

      <div className="page-toolbar">
        <div className="container-fluid">
          <div className="row g-3 align-items-center">
            <div className="col">
              <ol className="breadcrumb bg-transparent mb-0">
                <li className="breadcrumb-item"><Link to="/">Home</Link></li>
                <li className="breadcrumb-item"><Link to="/loan-customers">Customers</Link></li>
                <li className="breadcrumb-item active" aria-current="page">Document Management</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="page-body">
        <div className="container-fluid">
          {/* Customer Details Card */}
          <div className="customer-details-card">
            <div className="customer-details-header">
              <div className="customer-id">
                <span className="label">Customer</span>
                <h2>{customer?.customerNumber}</h2>
              </div>
              <div className="customer-status">
                <span className="badge bg-warning">{customer?.status}</span>
              </div>
            </div>
            <div className="customer-details-body">
              <div className="customer-details-grid">
                <div className="customer-detail-item">
                  <span className="detail-label">Company Name</span>
                  <span className="detail-value">{(customer?.info as any)?.registeredName || 'N/A'}</span>
                </div>
                <div className="customer-detail-item">
                  <span className="detail-label">Registration Number</span>
                  <span className="detail-value">{(customer?.info as any)?.registrationNumber || 'N/A'}</span>
                </div>
                <div className="customer-detail-item">
                  <span className="detail-label">Trading Name</span>
                  <span className="detail-value">{(customer?.info as any)?.tradingName || 'N/A'}</span>
                </div>
                <div className="customer-detail-item">
                  <span className="detail-label">Created Date</span>
                  <span className="detail-value">{customer?.createdAt ? new Date(customer.createdAt).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric'
                  }) : 'N/A'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Document Upload Card */}
          <div className="card">
            <div className="card-header">
              <h6 className="card-title">
                <i className="fa fa-file-alt text-primary me-2"></i>
                Document Management
              </h6>
            </div>
            <div className="card-body">
              <p className="text-muted mb-4">
                Manage the customer's business documents. You can upload, replace, or remove documents as needed. Ensure all documents are clear, legible, and properly verified.
              </p>

              <div className="alert alert-info mb-4">
                <div className="d-flex">
                  <div className="me-3">
                    <i className="fa fa-info-circle fa-lg"></i>
                  </div>
                  <div>
                    <p className="mb-0">
                      <strong>Document Requirements:</strong> PDF, JPG, PNG formats only. Maximum file size: 100MB per document.
                    </p>
                  </div>
                </div>
              </div>

              {uploadError && (
                <div className="alert alert-danger mb-4">
                  <div className="d-flex justify-content-between align-items-center">
                    <span>{uploadError}</span>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setUploadError(null)}
                      aria-label="Close"
                    ></button>
                  </div>
                </div>
              )}

              <div className="document-section mb-4">
                <h4 className="section-title mb-3">Required Documents</h4>
                <p className="text-muted mb-3">These documents are required for customer verification and onboarding. Documents marked with <span className="text-danger">*</span> are mandatory.</p>
                <div className="row g-4">
                  <div className="col-md-6">
                    <div className="document-upload-card">
                      <div className="document-info">
                        <h6 className="document-title">
                          <i className="fa fa-file-alt"></i>
                          Company Registration Certificate <span className="text-danger">*</span>
                        </h6>
                        <p className="document-description">CIPC certificate of incorporation or registration</p>
                        {getDocumentByType('COMPANY_REGISTRATION_CERTIFICATE') ? (
                          <div className="document-meta">
                            <i className="fa fa-check-circle text-success me-2"></i>
                            <span>{getDocumentByType('COMPANY_REGISTRATION_CERTIFICATE')?.name}</span>
                            <span className="bullet">•</span>
                            <span>{getDocumentByType('COMPANY_REGISTRATION_CERTIFICATE')?.size}</span>
                          </div>
                        ) : (
                          <p className="text-danger mb-0"><small>This document is required</small></p>
                        )}
                      </div>
                      <div className="document-upload">
                        <input
                          type="file"
                          id="file-company-registration"
                          className="d-none"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => handleFileChange(e, 'COMPANY_REGISTRATION_CERTIFICATE')}
                          disabled={isUploading}
                        />
                        <label htmlFor="file-company-registration" className={`btn ${getDocumentByType('COMPANY_REGISTRATION_CERTIFICATE') ? 'btn-success' : 'btn-outline-primary'}`}>
                          <i className={`fa ${getDocumentByType('COMPANY_REGISTRATION_CERTIFICATE') ? 'fa-check' : 'fa-upload'} me-2`}></i>
                          {getDocumentByType('COMPANY_REGISTRATION_CERTIFICATE') ? 'Replace File' : 'Upload File'}
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="document-upload-card">
                      <div className="document-info">
                        <h6 className="document-title">
                          <i className="fa fa-id-card"></i>
                          Company Representative ID <span className="text-danger">*</span>
                        </h6>
                        <p className="document-description">ID copy of company representative if not a director</p>
                        {getDocumentByType('COMPANY_REP_ID_COPY') ? (
                          <div className="document-meta">
                            <i className="fa fa-check-circle text-success me-2"></i>
                            <span>{getDocumentByType('COMPANY_REP_ID_COPY')?.name}</span>
                            <span className="bullet">•</span>
                            <span>{getDocumentByType('COMPANY_REP_ID_COPY')?.size}</span>
                          </div>
                        ) : (
                          <p className="text-danger mb-0"><small>This document is required</small></p>
                        )}
                      </div>
                      <div className="document-upload">
                        <input
                          type="file"
                          id="file-company-rep-id-copy"
                          className="d-none"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => handleFileChange(e, 'COMPANY_REP_ID_COPY')}
                          disabled={isUploading}
                        />
                        <label htmlFor="file-company-rep-id-copy" className={`btn ${getDocumentByType('COMPANY_REP_ID_COPY') ? 'btn-success' : 'btn-outline-primary'}`}>
                          <i className={`fa ${getDocumentByType('COMPANY_REP_ID_COPY') ? 'fa-check' : 'fa-upload'} me-2`}></i>
                          {getDocumentByType('COMPANY_REP_ID_COPY') ? 'Replace File' : 'Upload File'}
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="document-upload-card">
                      <div className="document-info">
                        <h6 className="document-title">
                          <i className="fa fa-university"></i>
                          Bank Letter <span className="text-danger">*</span>
                        </h6>
                        <p className="document-description">Confirmation of bank account details</p>
                        {getDocumentByType('BANK_LETTER') ? (
                          <div className="document-meta">
                            <i className="fa fa-check-circle text-success me-2"></i>
                            <span>{getDocumentByType('BANK_LETTER')?.name}</span>
                            <span className="bullet">•</span>
                            <span>{getDocumentByType('BANK_LETTER')?.size}</span>
                          </div>
                        ) : (
                          <p className="text-danger mb-0"><small>This document is required</small></p>
                        )}
                      </div>
                      <div className="document-upload">
                        <input
                          type="file"
                          id="file-bank-letter"
                          className="d-none"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => handleFileChange(e, 'BANK_LETTER')}
                          disabled={isUploading}
                        />
                        <label htmlFor="file-bank-letter" className={`btn ${getDocumentByType('BANK_LETTER') ? 'btn-success' : 'btn-outline-primary'}`}>
                          <i className={`fa ${getDocumentByType('BANK_LETTER') ? 'fa-check' : 'fa-upload'} me-2`}></i>
                          {getDocumentByType('BANK_LETTER') ? 'Replace File' : 'Upload File'}
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="document-upload-card">
                      <div className="document-info">
                        <h6 className="document-title">
                          <i className="fa fa-map-marker-alt"></i>
                          Proof of Address <span className="text-danger">*</span>
                        </h6>
                        <p className="document-description">Utility bill or lease agreement (not older than 3 months)</p>
                        {getDocumentByType('PROOF_OF_ADDRESS') ? (
                          <div className="document-meta">
                            <i className="fa fa-check-circle text-success me-2"></i>
                            <span>{getDocumentByType('PROOF_OF_ADDRESS')?.name}</span>
                            <span className="bullet">•</span>
                            <span>{getDocumentByType('PROOF_OF_ADDRESS')?.size}</span>
                          </div>
                        ) : (
                          <p className="text-danger mb-0"><small>This document is required</small></p>
                        )}
                      </div>
                      <div className="document-upload">
                        <input
                          type="file"
                          id="file-proof-of-address"
                          className="d-none"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => handleFileChange(e, 'PROOF_OF_ADDRESS')}
                          disabled={isUploading}
                        />
                        <label htmlFor="file-proof-of-address" className={`btn ${getDocumentByType('PROOF_OF_ADDRESS') ? 'btn-success' : 'btn-outline-primary'}`}>
                          <i className={`fa ${getDocumentByType('PROOF_OF_ADDRESS') ? 'fa-check' : 'fa-upload'} me-2`}></i>
                          {getDocumentByType('PROOF_OF_ADDRESS') ? 'Replace File' : 'Upload File'}
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="document-upload-card">
                      <div className="document-info">
                        <h6 className="document-title">
                          <i className="fa fa-file-invoice"></i>
                          Tax Clearance <span className="text-danger">*</span>
                        </h6>
                        <p className="document-description">Company tax clearance</p>
                        {getDocumentByType('TAX_CLEARANCE') ? (
                          <div className="document-meta">
                            <i className="fa fa-check-circle text-success me-2"></i>
                            <span>{getDocumentByType('TAX_CLEARANCE')?.name}</span>
                            <span className="bullet">•</span>
                            <span>{getDocumentByType('TAX_CLEARANCE')?.size}</span>
                          </div>
                        ) : (
                          <p className="text-danger mb-0"><small>This document is required</small></p>
                        )}
                      </div>
                      <div className="document-upload">
                        <input
                          type="file"
                          id="file-tax-clearance"
                          className="d-none"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => handleFileChange(e, 'TAX_CLEARANCE')}
                          disabled={isUploading}
                        />
                        <label htmlFor="file-tax-clearance" className={`btn ${getDocumentByType('TAX_CLEARANCE') ? 'btn-success' : 'btn-outline-primary'}`}>
                          <i className={`fa ${getDocumentByType('TAX_CLEARANCE') ? 'fa-check' : 'fa-upload'} me-2`}></i>
                          {getDocumentByType('TAX_CLEARANCE') ? 'Replace File' : 'Upload File'}
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="document-upload-card">
                      <div className="document-info">
                        <h6 className="document-title">
                          <i className="fa fa-user-shield"></i>
                          Delegation of Authority
                        </h6>
                        <p className="document-description">Delegation of authority if representative is not a director</p>
                        {getDocumentByType('DELEGATION_OF_AUTHORITY') ? (
                          <div className="document-meta">
                            <i className="fa fa-check-circle text-success me-2"></i>
                            <span>{getDocumentByType('DELEGATION_OF_AUTHORITY')?.name}</span>
                            <span className="bullet">•</span>
                            <span>{getDocumentByType('DELEGATION_OF_AUTHORITY')?.size}</span>
                          </div>
                        ) : null}
                      </div>
                      <div className="document-upload">
                        <input
                          type="file"
                          id="file-delegation-of-authority"
                          className="d-none"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => handleFileChange(e, 'DELEGATION_OF_AUTHORITY')}
                          disabled={isUploading}
                        />
                        <label htmlFor="file-delegation-of-authority" className={`btn ${getDocumentByType('DELEGATION_OF_AUTHORITY') ? 'btn-success' : 'btn-outline-primary'}`}>
                          <i className={`fa ${getDocumentByType('DELEGATION_OF_AUTHORITY') ? 'fa-check' : 'fa-upload'} me-2`}></i>
                          {getDocumentByType('DELEGATION_OF_AUTHORITY') ? 'Replace File' : 'Upload File'}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Uploaded Documents List */}
              <div className="document-section mt-5">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h6 className="section-subtitle mb-0">
                    <i className="fa fa-list me-2"></i>
                    Uploaded Documents
                  </h6>
                </div>

                {!documents?.length ? (
                  <div className="alert alert-warning">
                    <i className="fa fa-exclamation-triangle me-2"></i>
                    No documents uploaded yet.
                  </div>
                ) : (
                  <div className="table-responsive">
                    <DataTable
                      columns={columns}
                      data={documentsData}
                      pagination
                      highlightOnHover
                      striped
                      responsive
                      noDataComponent={
                        <div className="text-center py-4">
                          <i className="fa fa-folder text-muted" style={{ fontSize: '2rem' }}></i>
                          <p className="mt-2">No documents found</p>
                        </div>
                      }
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <DocumentPreviewModal preview={documentPreview.preview} onClose={documentPreview.close} />
    </div>
  );
};

export default CustomerDocuments;
